import { createWebHistory, createRouter } from "vue-router";
import axios from "axios";
import routes from "./routes";
import appConfig from "../../app.config";
import Cookie from "js-cookie";
import { useAuthStore } from "@/stores/auth";
import { topProgressBar } from "@/helpers/top-progress";

axios.defaults.withCredentials = true;
axios.defaults.baseURL = process.env.VUE_APP_BACKEND_API_URL || "http://localhost:8100";

const router = createRouter({
  history: createWebHistory(),
  routes,
  // Use the HTML5 history API (i.e. normal-looking routes)
  // instead of routes with hashes (e.g. example.com/#/about).
  // This may require some server configuration in production:
  // https://router.vuejs.org/en/essentials/history-mode.html#example-server-configurations
  mode: "history",
  // Simulate native-like scroll behavior when navigating to a new
  // route and using back/forward buttons.
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { top: 0, left: 0 };
    }
  },
});

// API based Authentication
// Before each route evaluates...
router.beforeEach(async (routeTo, routeFrom, next) => {
  // Jalankan animasi progress bar di bagian atas layar saat rute mulai berpindah
  topProgressBar.start();

  const authStore = useAuthStore();

  const authRequired = routeTo.matched.some((route) => route.meta.authRequired);

  if (!authRequired) return next();

  // 1. Periksa sesi lokal user (Cookie user atau saa_token)
  const userCookie = Cookie.get("user");
  const tokenCookie = Cookie.get("saa_token");

  // Jika user belum login sama sekali, cegat rute dan langsung arahkan ke SSO
  // Halaman Sales / Dashboard TIDAK AKAN PERNAH dirender atau terlihat di browser
  if (!userCookie && !tokenCookie) {
    if (routeTo.fullPath && !routeTo.fullPath.includes("/login") && !routeTo.fullPath.includes("/auth/callback")) {
      sessionStorage.setItem("sso_redirect_url", routeTo.fullPath);
      Cookie.set("sso_redirect_url", routeTo.fullPath, { expires: 1, path: "/" });
    }
    const rawBackendUrl = process.env.VUE_APP_BACKEND_API_URL || window.location.origin;
    const backendOrigin = new URL(rawBackendUrl).origin;
    window.location.href = `${backendOrigin}/auth/sso`;
    return;
  }
  // Ambil meta data page_code dari rute tujuan (digunakan untuk identifikasi modul/halaman hak akses)
  const pageCode = routeTo.meta.page_code;
  // Ambil jenis hak akses minimal yang diperlukan (misal: 'can_read', 'can_create', dll.)
  const requiredAccess = routeTo.meta.required_access;
  // Periksa apakah rute ini dikecualikan dari pengecekan hak akses
  const skipPrivilegeCheck = routeTo.matched.some((route) => route.meta.skipPrivilegeCheck);
  // Kondisi bypass khusus untuk form create Pengajuan Plafond dengan parameter verifikasi/revisi
  const bypassPrivilegeCheck = routeTo.name === "PengajuanPlafondCreate" && (routeTo.query.verify_role !== undefined || routeTo.query.revise_id != undefined);

  // Jika rute ditandai untuk melewati pengecekan privilege, izinkan navigasi langsung
  if (skipPrivilegeCheck || bypassPrivilegeCheck) {
    return next();
  }

  // Jika halaman tidak memiliki page_code yang terdaftar
  if (!pageCode) {
    // Jika rute tujuan memang halaman 403, izinkan navigasi agar tidak terjadi redirect loop
    if (routeTo.path === "/auth/403") {
      return next();
    }
    // Jika tidak terdaftar page_code dan bukan 403, arahkan ke 403 Forbidden
    return next("/auth/403");
  }

  try {
    // Pastikan data privilege dari endpoint /me sudah ter-load sebelum mengecek rute
    if (!authStore.isPrivilegesLoaded) {
      await authStore.fetchMe();
    }

    // Ambil data hak akses melalui authStore dari in-memory cache
    const access = await authStore.getPrivilege(pageCode);

    // Jika user tidak memiliki hak akses sama sekali untuk halaman ini, redirect ke 403
    if (!access) {
      return next("/auth/403");
    }

    // Jika rute tidak mensyaratkan akses spesifik (misal hanya butuh page terdaftar), izinkan navigasi
    if (!requiredAccess) {
      return next();
    }

    // Cek apakah aksi yang diminta bernilai true (mendukung format 'create' maupun 'can_create')
    const isAllowed =
      access[requiredAccess] === true ||
      access[requiredAccess.replace(/^can_/, "")] === true ||
      access[`can_${requiredAccess}`] === true;

    if (isAllowed) {
      // Hak akses terpenuhi, izinkan user masuk ke halaman
      return next();
    }

    // Jika hak akses spesifik tidak terpenuhi, lempar ke halaman 403 Forbidden
    return next("/auth/403");
  } catch (error) {
    // Tangani jika terjadi error tak terduga, arahkan ke 403
    return next("/auth/403");
  }

  // Ada token, validasi dengan backend
  // try {
  //   axios.defaults.headers.common['authorization'] = 'Bearer ' + saaToken;
  //   const response = await axios.get('https://auth.pt-saa.com/api/me-sales');

  //   if (response.data.valid) {
  //     // Token valid, simpan user info dan lanjutkan
  //     Cookie.set('user', JSON.stringify(response.data.user), { expires: 90 });
  //     next();
  //   } else {
  //     // Token tidak valid, hapus dan redirect ke login
  //     Cookie.remove('saa_token');
  //     Cookie.remove('user');
  //     next({ name: 'login', query: { redirectFrom: routeTo.fullPath } });
  //   }
  // } catch (error) {
  //   // Error validasi token, hapus dan redirect ke login
  //   Cookie.remove('saa_token');
  //   Cookie.remove('user');
  //   next({ name: 'login', query: { redirectFrom: routeTo.fullPath } });
  // }
});

router.beforeResolve(async (routeTo, routeFrom, next) => {
  // Create a `beforeResolve` hook, which fires whenever
  // `beforeRouteEnter` and `beforeRouteUpdate` would. This
  // allows us to ensure data is fetched even when params change,
  // but the resolved route does not. We put it in `meta` to
  // indicate that it's a hook we created, rather than part of
  // Vue Router (yet?).
  try {
    // For each matched route...
    for (const route of routeTo.matched) {
      await new Promise((resolve, reject) => {
        // If a `beforeResolve` hook is defined, call it with
        // the same arguments as the `beforeEnter` hook.
        if (route.meta && route.meta.beforeResolve) {
          route.meta.beforeResolve(routeTo, routeFrom, (...args) => {
            // If the user chose to redirect...
            if (args.length) {
              // If redirecting to the same route we're coming from...
              // Complete the redirect.
              next(...args);
              reject(new Error("Redirected"));
            } else {
              resolve();
            }
          });
        } else {
          // Otherwise, continue resolving the route.
          resolve();
        }
      });
    }
    // If a `beforeResolve` hook chose to redirect, just return.
  } catch (error) {
    return;
  }
  document.title = routeTo.meta.title + " | " + appConfig.title;
  // If we reach this point, continue resolving the route.
  next();
});

// Selesaikan animasi loading setelah rute selesai dimuat dan bersihkan overlay yang tertinggal
router.afterEach((to, from, failure) => {
  // Pastikan overlay mobile dan modal backdrop tidak tertinggal di body saat ganti halaman
  if (typeof document !== "undefined") {
    document.body.classList.remove("vertical-sidebar-enable");
    document.body.classList.remove("modal-open");

    // Hapus semua orphan backdrop dan BOverlay dari body yang ditinggal setelah navigasi
    document
      .querySelectorAll(
        ".modal-backdrop, body > .b-overlay, body > .b-overlay-wrap"
      )
      .forEach((el) => el.remove());
  }

  // Reset isModalOpen di Pinia store distributionPlan agar BModal tidak terus di-teleport ke body
  // setelah user navigasi keluar dari halaman create displan
  try {
    const { useDistributionPlanStore } = require("@/stores/distributionPlan");
    const dpStore = useDistributionPlanStore();
    if (dpStore && dpStore.isModalOpen) {
      dpStore.isModalOpen = false;
    }
  } catch (e) {
    // Store mungkin belum diinisialisasi, abaikan
  }

  if (failure) {
    topProgressBar.fail();
  } else {
    topProgressBar.finish();
  }
});

// Tangani jika terjadi error fatal saat memuat chunk komponen rute
router.onError(() => {
  topProgressBar.fail();
});

export default router;
